import Aura from '@primeng/themes/aura';
import { EnEventStatus } from '../dto';

/**
 * Aura primitive palette used for calendar events — mirrors the `colors` map on
 * {@link AbstractBaseComponent} so events look identical wherever they render.
 */
const colors = {
  slate: Aura.primitive!.slate!,
  blue: Aura.primitive!.blue!,
  green: Aura.primitive!.green!,
  orange: Aura.primitive!.orange!,
  red: Aura.primitive!.red!,
  yellow: Aura.primitive!.yellow!,
};

/**
 * Event title ink. A theme token, not a fixed hex: the fill is transparent, so the
 * title sits on the calendar cell and has to follow it into dark mode — a hardcoded
 * near-black was unreadable there.
 */
const EVENT_TITLE_TEXT = 'var(--p-text-color)';

/** Figma event-card: outline only — no fill. */
const EVENT_BACKGROUND = 'transparent';

type AuraScale = (typeof colors)[keyof typeof colors];

export interface EventColors {
  /** Always transparent — colour coding lives on the border. */
  background: string;
  border: string;
  text: string;
}

/**
 * Outline-only treatment for one Aura colour scale (Figma event-card: border-2,
 * no background fill).
 */
function outlinedEventColors(palette: AuraScale, step: '400' | '600' = '600'): EventColors {
  return {
    background: EVENT_BACKGROUND,
    border: palette[step]!,
    text: EVENT_TITLE_TEXT,
  };
}

/**
 * Maps an event {@link EnEventStatus} to its FullCalendar colours.
 *
 * Single source of truth shared by the assignment scheduling calendar and the
 * read-only dashboard calendar.
 *
 * The rule: the ORDINARY state is neutral, and a colour is spent only where
 * something is being said. `SCHEDULED` used to be yellow, which made a calendar of
 * perfectly healthy future sedute read as a wall of warnings — and left no colour
 * for the one state that genuinely calls for attention. Yellow now belongs to
 * `DELAYED`: late, but still recoverable, and the only pending state an operator
 * can still act on.
 *
 * Note the previous fallback hid two real cases: `DELAYED` and `NOT_COMPLETED`
 * both fell through to green, so a missed appointment and an unfinished session
 * looked exactly like a completed one. Every status is enumerated now, and the
 * default is deliberately neutral rather than reassuring.
 */
export function eventStatusColors(status?: EnEventStatus): EventColors {
  switch (status) {
    case EnEventStatus.COMPLETED:
      return outlinedEventColors(colors.green);
    case EnEventStatus.INPROGRESS:
      return outlinedEventColors(colors.blue);
    case EnEventStatus.DELAYED:
      return outlinedEventColors(colors.yellow);
    // Started and left unfinished, or deliberately skipped: both are outcomes the
    // patient did not complete, but neither is a no-show.
    case EnEventStatus.NOTCOMPLETED:
    case EnEventStatus.SKIPPED:
      return outlinedEventColors(colors.orange);
    // The only one that means nobody turned up.
    case EnEventStatus.MISSED:
      return outlinedEventColors(colors.red);
    // History: a seduta moved away (its replacement carries the appointment) or
    // cancelled. Both are filtered off the live calendars; this is what they look
    // like in a view that deliberately shows the past.
    case EnEventStatus.RESCHEDULED:
    case EnEventStatus.DELETED:
      // A lighter step of the same neutral: history should sit behind what is
      // still live, not next to it.
      return outlinedEventColors(colors.slate, '400');
    // SCHEDULED and anything new: the ordinary state says nothing, so it shows
    // nothing.
    case EnEventStatus.SCHEDULED:
    default:
      return outlinedEventColors(colors.slate);
  }
}
